import React, { useState, useEffect } from 'react';

export default function Molton8BitPortfolio() {
  const [activeTab, setActiveTab] = useState('intro');
  const [pixelEffect, setPixelEffect] = useState(0);
  
  // Simple pixel animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setPixelEffect(prev => (prev + 1) % 4);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gray-800 text-green-400 font-mono p-4 overflow-hidden">
      {/* 8-bit styled header */}
      <header className="w-full max-w-4xl mx-auto mb-8 text-center">
        <div className="flex items-center justify-center mb-4">
          <div className="text-4xl font-bold tracking-wider bg-gray-900 px-6 py-2 border-4 border-green-500">
            <span className="text-green-300">MOLTON</span>
            <span className="text-gray-400">64</span>
          </div>
        </div>
        
        {/* Pixel border */}
        <div className="flex w-full">
          {[...Array(32)].map((_, i) => (
            <div 
              key={i} 
              className={`h-4 w-full ${(i + pixelEffect) % 4 === 0 ? 'bg-green-600' : 'bg-green-500'}`}
            ></div>
          ))}
        </div>
      </header>

      {/* Portfolio intro section */}
      <section className="w-full max-w-4xl mx-auto mb-8">
        <div className="border-4 border-green-500 bg-gray-900 p-6">
          <h1 className="text-3xl font-bold text-green-400 mb-6 tracking-widest">MOLTON'S PORTFOLIO</h1>
          
          <div className="space-y-4 text-green-300 leading-relaxed">
            <p>
              I AM A COLLEGE STUDENT WHO ENJOYS TO CREATE TEXTURES AND MAKE MAPS FOR GAMES. 
              I USE THE UNREAL ENGINE FOR GAME DEVELOPMENT AND I USE ADOBE SUBSTANCE PAINTER 
              TO CREATE MY TEXTURES.
            </p>
            
            <p className="text-yellow-300 font-bold">
              **MODELS SHOWN IN THIS PORTFOLIO ARE NOT MADE BY ME AND ARE USED AS A BASE FOR CUSTOM TEXTURES**
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="w-full max-w-4xl mx-auto mb-2">
        <div className="flex">
          <button
            className={`px-6 py-3 border-t-4 border-l-4 border-r-4 ${activeTab === 'intro' ? 'bg-green-700 text-green-200 border-green-300' : 'bg-gray-700 border-gray-600 hover:bg-gray-600'}`}
            onClick={() => setActiveTab('intro')}
          >
            INTRO
          </button>
          <button
            className={`px-6 py-3 border-t-4 border-l-4 border-r-4 ${activeTab === 'retextures' ? 'bg-green-700 text-green-200 border-green-300' : 'bg-gray-700 border-gray-600 hover:bg-gray-600'}`}
            onClick={() => setActiveTab('retextures')}
          >
            RETEXTURES
          </button>
          <button
            className={`px-6 py-3 border-t-4 border-l-4 border-r-4 ${activeTab === 'environmental' ? 'bg-green-700 text-green-200 border-green-300' : 'bg-gray-700 border-gray-600 hover:bg-gray-600'}`}
            onClick={() => setActiveTab('environmental')}
          >
            ENVIRONMENTAL ART
          </button>
          <button
            className={`px-6 py-3 border-t-4 border-l-4 border-r-4 ${activeTab === 'imageShowcase' ? 'bg-green-700 text-green-200 border-green-300' : 'bg-gray-700 border-gray-600 hover:bg-gray-600'}`}
            onClick={() => setActiveTab('imageShowcase')}
          >
            SHOWCASE
          </button>
          <div className="flex-grow border-b-4 border-green-500"></div>
        </div>
      </div>

      {/* Tab Content */}
      <main className="w-full max-w-4xl mx-auto flex-grow">
        <div className="border-4 border-green-500 bg-gray-900 p-4">
          {activeTab === 'intro' ? (
            <IntroTab />
          ) : activeTab === 'retextures' ? (
            <RetexturesTab />
          ) : activeTab === 'imageShowcase' ? (
            <ImageShowcaseTab />
          ) : (
            <EnvironmentalTab />
          )}
        </div>
      </main>
      
      {/* 8-bit styled footer */}
      <footer className="w-full max-w-4xl mx-auto mt-8 text-center">
        <div className="flex w-full mb-4">
          {[...Array(32)].map((_, i) => (
            <div 
              key={i} 
              className={`h-4 w-full ${(i + pixelEffect) % 4 === 0 ? 'bg-green-600' : 'bg-green-500'}`}
            ></div>
          ))}
        </div>
        <p className="text-green-300 text-sm">© 2025 MOLTON'S PORTFOLIO</p>
      </footer>
    </div>
  );
}

function IntroTab() {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-green-400 tracking-widest">WELCOME!</h1>
      
      <div className="space-y-4 text-green-300 leading-relaxed">
        <p>
          WELCOME TO MY RETEXTURE AND ENVIRONMENTAL ART PORTFOLIO. HERE YOU'LL FIND
          EXAMPLES OF MY WORK USING ADOBE SUBSTANCE PAINTER AND UNREAL ENGINE.
        </p>
        
        <p>
          I SPECIALIZE IN CREATING DETAILED TEXTURES THAT GIVE LIFE AND CHARACTER
          TO 3D MODELS AND ENVIRONMENTS.
        </p>
        
        <div className="my-8 border-4 border-green-500 p-4">
          <h2 className="text-xl font-bold text-green-400 mb-4">MY SKILLS:</h2>
          <ul className="grid grid-cols-2 gap-4">
            <li className="bg-gray-800 p-2 border-2 border-green-600">SUBSTANCE PAINTER</li>
            <li className="bg-gray-800 p-2 border-2 border-green-600">UNREAL ENGINE</li>
            <li className="bg-gray-800 p-2 border-2 border-green-600">MATERIAL CREATION</li>
            <li className="bg-gray-800 p-2 border-2 border-green-600">ENVIRONMENTAL DESIGN</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
function RetexturesTab() {
    // In a real implementation, this would fetch or manage your texture works
    const [textureWorks, setTextureWorks] = useState([
      { id: 1, title: "RUSTED METAL", description: "CUSTOM RUST TEXTURE FOR INDUSTRIAL OBJECTS" },
    ]);
  
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-green-400 tracking-widest">RETEXTURES</h1>
          <button className="bg-green-700 px-4 py-2 border-2 border-green-500 hover:bg-green-600">ADD NEW</button>
        </div>
  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {textureWorks.map(work => (
            <div key={work.id} className="border-4 border-green-500 p-4 bg-gray-800">
              <div className="h-48 bg-gray-700 mb-4 flex items-center justify-center">
                <div className="text-green-500 font-bold text-4xl">{work.title}</div>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-green-300 font-bold">{work.title}</div>
                  <div className="text-green-400 text-sm mt-1">{work.description}</div>
                </div>
                <button className="bg-green-800 px-3 py-1 border-2 border-green-600 hover:bg-green-700 text-sm">EDIT</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  function EnvironmentalTab() {
    // In a real implementation, this would fetch or manage your environmental works
    const [environmentWorks, setEnvironmentWorks] = useState([
      { 
        id: 1, 
        title: "ABANDONED FACTORY", 
        description: "INDUSTRIAL ENVIRONMENT WITH CUSTOM TEXTURES",
        image: "abandoned_factory.png",
        thumbnails: [
          "factory_exterior.png",
        ]
      },
      
    ]);
  
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-green-400 tracking-widest">ENVIRONMENTAL ART</h1>
          <button className="bg-green-700 px-4 py-2 border-2 border-green-500 hover:bg-green-600">ADD NEW</button>
        </div>
        
        
        <div className="grid grid-cols-1 gap-6 mt-6">
          {environmentWorks.map(work => (
            <div key={work.id} className="border-4 border-green-500 p-4 bg-gray-800">
              {/* Main image representation */}
              <div className="h-64 bg-gray-700 mb-4 flex items-center justify-center">
                <div className="text-green-500 font-bold text-xl">[{work.image}]</div>
              </div>
              
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="text-green-300 font-bold">{work.title}</div>
                  <div className="text-green-400 text-sm mt-1">{work.description}</div>
                </div>
                <button className="bg-green-800 px-3 py-1 border-2 border-green-600 hover:bg-green-700 text-sm">EDIT</button>
              </div>
              
              {/* Thumbnail gallery */}
              <div className="mb-4">
                <h3 className="text-green-400 text-sm mb-2">DETAIL VIEWS:</h3>
                <div className="grid grid-cols-3 gap-2">
                  {work.thumbnails.map((thumbnail, index) => (
                    <div key={index} className="border border-green-600 p-1">
                      <div className="h-20 bg-gray-700 flex items-center justify-center">
                        <div className="text-green-500 text-xs">[{thumbnail}]</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
        
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  function ImageShowcaseTab() {
    // This component is for demonstration purposes only to show images
    // The images are not actually implemented in the site
    const [showcaseImages, setShowcaseImages] = useState([
      { 
        id: 1, 
        title: "RUSTED MACHINERY", 
        description: "BEFORE AND AFTER RETEXTURE COMPARISON",
        type: "comparison",
        beforeImage: "rust_texture_before.png",
        afterImage: "rust_texture_after.png"
      },
    ]);
  
    return (
      <div className="space-y-6">
       
        
        <div className="grid grid-cols-1 gap-8 mt-6">
          {showcaseImages.map(item => (
            <div key={item.id} className="border-4 border-green-500 p-4 bg-gray-800">
              <h2 className="text-green-300 font-bold text-xl mb-2">{item.title}</h2>
              <p className="text-green-400 text-sm mb-4">{item.description}</p>
              
              {item.type === "comparison" ? (
                <div className="grid grid-cols-2 gap-4">
                  {/* Before image representation */}
                  <div className="border-2 border-gray-600 p-2">
                    <div className="text-center mb-2 text-gray-400">BEFORE</div>
                    <div className="h-64 bg-gray-700 flex items-center justify-center">
                      <div className="text-gray-500">[{item.beforeImage}]</div>
                    </div>
                  </div>
                  
                  {/* After image representation */}
                  <div className="border-2 border-green-600 p-2">
                    <div className="text-center mb-2 text-green-400">AFTER</div>
                    <div className="h-64 bg-gray-700 flex items-center justify-center">
                      <div className="text-green-500">[{item.afterImage}]</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-green-600 p-2">
                  <div className="h-64 bg-gray-700 flex items-center justify-center">
                    <div className="text-green-500">[{item.image}]</div>
                  </div>
                </div>
              )}
              
              <div className="mt-4 flex space-x-2">
                <button className="bg-green-800 px-3 py-1 border-2 border-green-600 hover:bg-green-700 text-sm">METADATA</button>
                <button className="bg-green-800 px-3 py-1 border-2 border-green-600 hover:bg-green-700 text-sm">FULL SIZE</button>
                <button className="bg-green-800 px-3 py-1 border-2 border-green-600 hover:bg-green-700 text-sm">DETAILS</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }